import React, { useEffect, useState } from 'react';
import "./terminalDetails.scss";
import DetailsWidget from './details/detailsSection';
import AdditionalSettings from './additionalSettings/additionalSection';
import ContentContainer from '../../templates/content-container/contentContainer';
import { useDispatch, useSelector } from 'react-redux';
import { getTerminalSummaryByTerminalNumber, terminalDetailsSelector } from '../../slices/terminal-summary/terminalDetailsSlice';
import { TerminalState } from '../../slices/terminal-summary/TerminalDetailsState';
import { useParams } from 'react-router-dom';
import { PathParams, TerminalSummaryPath } from './route';
import { getAdditionalSettings } from 'slices/terminal-summary/additionalSettingsSlice';
import TerminalChangelog from './changelog/terminalChangelog';
import BasicInfo from 'features/terminal-details/basicInfo/basicInfo';
import ThreeDotsMenu from 'ui-components/three-dots-menu/threeDotsMenu';
import { ReactComponent as EditIcon } from "images/edit_outline.svg"
import { ReactComponent as ToggleIcon } from "images/toggle_solid.svg"
import { ReactComponent as TrashIcon } from "images/trash.svg"
import DeleteTerminal from './dropdownMenu/deleteTerminal';
import EditTerminalName from './dropdownMenu/editTerminalName';
import EditStatus from './dropdownMenu/editStatus';
import Loading from 'ui-components/loading/loading';
import { UserUtils } from 'app/data/user/userUtils';
import { userSelector } from 'slices/user/userSlice';
import UserState from 'slices/user/UserState';
import ImportantMessage, { ImportantMessageTheme } from 'ui-components/molecules/important-message/importantMessage';
import { QUERY_KEYS } from 'app/data/common/queryKeys';
import { useQuery } from '@tanstack/react-query';
import TerminalService from 'app/data/terminal-details/terminalService';

const tabData = [
    { label: 'Details', content: <DetailsWidget /> },
    { label: 'Terminal Settings', content: <AdditionalSettings /> },
    { label: 'Change Log', content: <TerminalChangelog /> },
];

const Tabs = (): React.ReactElement => {
    const [activeTab, setActiveTab] = useState(0);

    return (
        <div className="tabs">
            <div className="tabs__buttons">
                {tabData.map((tab, index) => (
                    <button
                        key={index}
                        className={`tabs__button ${activeTab === index ? 'active' : ''}`}
                        onClick={() => setActiveTab(index)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            <div className="tabs__content">
                {tabData[activeTab].content}
            </div>
        </div>
    );
};

const TerminalDetails = (): React.ReactElement => {
    const dispatch = useDispatch();
    const params = useParams() as PathParams;
    const terminalNumber = params[TerminalSummaryPath.number];
    const terminalState: TerminalState = useSelector(terminalDetailsSelector);
    const userState: UserState = useSelector(userSelector);

    const [showModal, setShowModal] = useState<boolean>(false);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [showInactive, setShowInactive] = useState<boolean>(false);

    const { data: deletionRequest, refetch } = useQuery({
        queryKey: [QUERY_KEYS.TERMINAL_DELETION_REQUEST, terminalNumber],
        queryFn: async () => {
            const response = await TerminalService.getInstance().getTerminalDeletionRequest(terminalNumber);
            return response;
        },
        onSuccess: (data) => {
        },
        enabled: !!terminalNumber,
        refetchOnWindowFocus: false,
        retry: false,
    });

    useEffect(() => {
        if (terminalNumber) {
            dispatch(getTerminalSummaryByTerminalNumber(terminalNumber));
            dispatch(getAdditionalSettings(terminalNumber));
        }
    }, [terminalNumber, dispatch]);
    
    const getDisabledDeleteButton = () => {
        return deletionRequest ? deletionRequest?.status !== 'REJECTED' : false;
    }

    return (
        <div className="terminal-details">
            <ContentContainer
                titleComponent={
                    <>
                        <div className="xgs-site__content-container__title-container">
                            {terminalState.data?.terminalName ? <> <span className="xgs-site__content-container__title">
                                {terminalState.data?.terminalName}
                            </span>
                                <span className={'xgs-site__content-container__title__status ' + (terminalState.data?.status === "ACTIVE" ? "xgs-site__content-container__title__status--active" : "xgs-site__content-container__title__status--inactive")}>{terminalState.data?.status}</span>
                                {UserUtils.isXGSAdministrator(userState.profile) && <span className="xgs-site__content-container__page-menu">
                                    <ThreeDotsMenu
                                        separator={true}
                                        separatorIndex={1}
                                        menuItems={[
                                            {
                                                text: "Edit Terminal Name",
                                                icon: <EditIcon width={12} height={12} style={{ color: "black" }} />,
                                                onClick: () => { setShowEditModal(true) }
                                            },
                                            {
                                                text: "Mark as " + (terminalState.data?.status === "ACTIVE" ? "Inactive" : "Active"),
                                                icon: <ToggleIcon width={12} height={12} style={{ color: "black" }} />,
                                                onClick: () => { setShowInactive(true) }
                                            },
                                            {
                                                text: <div style={{ color: getDisabledDeleteButton() ? "rgba(79, 95, 114, 0.32)" : "#FF0000" }}>Delete Terminal</div>,
                                                icon: <TrashIcon width={12} height={12} style={{ color: getDisabledDeleteButton() ? "rgba(79, 95, 114, 0.32)" : "#FF0000" }} />,
                                                onClick: () => { setShowModal(true) },
                                                disabled: getDisabledDeleteButton()
                                            }
                                        ]}
                                    /></span>}
                            </> : <Loading isLoading={true} />}
                        </div>
                        {deletionRequest && deletionRequest?.status !== 'REJECTED' && <ImportantMessage theme={ImportantMessageTheme.BLUE} mix="xgs-pickup__message-row">
                            <div>Deletion request submitted on {deletionRequest?.dateCreated?.toString()?.toUiDateFormat()}. Awaiting approval.</div>
                        </ImportantMessage>}
                        <div className='grid-layout'>
                            <div className="grid-layout__left">
                                <BasicInfo />
                            </div>
                            <div className="grid-layout__right">
                                <Tabs />
                            </div>
                        </div>
                    </>
                }
            ></ContentContainer>
            <DeleteTerminal showModal={showModal}
                handleClose={() => setShowModal(false)}
                refetchDeletionRequest={refetch}
                terminalName={terminalState.data?.terminalName} />
            <EditTerminalName showModal={showEditModal}
                handleClose={() => setShowEditModal(false)}
                terminalName={terminalState.data?.terminalName} />
            <EditStatus
                showModal={showInactive}
                handleClose={() => setShowInactive(false)}
            />
        </div>
    );
};

export default TerminalDetails;
